import LashbrookLogo from '../assets/images/lashbrook.svg';
import BenBridgeLogo from '../assets/images/ben-bridge.svg';
import { matchPath } from 'react-router-dom';
import { IParams } from '../components/RingCustomizerHOC';
import { IOption } from '../types/customizer';
import { PATH_PREFIX } from '../config/tenant';

import weddingBand from '../assets/images/wedding-bands.png';
import anniversary from '../assets/images/anniversary.png';
import signet from '../assets/images/signet.png';
import collegiateSignets from '../assets/images/collegiate-signets.jpg';
import collegiateBand from '../assets/images/collegiate-band.jpg';
import collegiateSleeve from '../assets/images/collegiate-sleeve.png';

//all client names in Lowercase
export const CLIENTS = [PATH_PREFIX];

// Storefront hostname per client, used to build https://www.<domain>.com/ links.
// The path prefix is NOT always the domain: Ben Bridge is served under
// /ben-bridge/ but its site is benbridge.com (unhyphenated). Clients absent from
// this map fall back to the prefix itself.
export const CLIENT_DOMAINS: { [key: string]: string } = {
  [PATH_PREFIX]: 'benbridge'
};

export const isEmpty = (value: any) =>
  value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);

export type AnyObject = { [key: string]: any };

export const lowercaseKeys = (obj: AnyObject, deep = true) =>
  Object.keys(obj).reduce((acc, key) => {
    const convertedKey = key.replace(/\s+/g, '_').toLowerCase();
    acc[convertedKey] = deep && typeof obj[key] === 'object' ? lowercaseKeys(obj[key]) : obj[key];
    return acc;
  }, {} as AnyObject);

export const defaultRingUrls = {
  band: '/rings/profiles/D/widths/7/ring_materials/14KW?ring_finish=SATIN&finish_type=F&weight=P&size=10&feature_group=N&sleeve=NONE',
  womens:
    '/women/rings/styles/010/ring_materials/14KW/color_arrangements/B/stone_quantities/HALFETERNITY/variants/2?size=7&dia=LABDIA-ST',
  collegiate_signet:
    '/rings/collegiate_signet/ring_materials/SILVER?ring_finish=POLISH,POLISH,&shape=CIRC&size=SM&shank=T&school=BYU&signet_engraving=BYU2&cerakote_color=NONE&finger_size=10&weight=S',
  signet:
    '/rings/signet/ring_materials/SILVER?ring_finish=POLISH,POLISH,&shape=CIRC&size=SM&shank=T&finger_size=10&feature_group=N&weight=S',
  collegiate_bands: '/rings/collegiate_bands/profiles/D/widths/7/ring_materials/PLAT?weight=P&size=10&feature_group=N%2CM&machine_pattern=%2CAIRFORCE1_PLAT_COLOR_BLACK&sleeve=NONE&school=AIRFORCE',
};

// Used whenever `storeData` is null — both before the store-status fetch resolves
// and if it rejects. Mirrors the values previously hardcoded at the getPageData
// call sites, so a dead endpoint looks the same as before rather than Band-only.
const FALLBACK_BUILDER_FLAGS = {
  show_women_builder: false,
  show_signet_builder: true,
  show_collegiate_builder: true
};

export const getCategorizedOptions = (sku: string, storeData: AnyObject | null) => {
  // Spread, not `storeData ||`, so a response missing a flag falls back per-key
  // instead of hiding that builder. Any flag the API sends wins.
  const { show_women_builder, show_signet_builder, show_collegiate_builder } = {
    ...FALLBACK_BUILDER_FLAGS,
    ...(storeData || {})
  };

  const list: IOption[] = [];
  list.push({
    name: 'Band',
    type: 'option',
    swatch_url: weddingBand,
    video_url: 'https://lashbrook-customizer-prod.s3.us-west-2.amazonaws.com/categories/Band.mp4',
    selected: false,
    sku: 'band'
  });
  if (show_women_builder) {
    list.push({
      name: 'Anniversary',
      type: 'option',
      swatch_url: anniversary,
      video_url: 'https://lashbrook-customizer-prod.s3.us-west-2.amazonaws.com/categories/Anniversary.mp4',
      selected: false,
      sku: 'womens'
    });
  }
  if (show_signet_builder) {
    list.push({
      name: 'Signet',
      type: 'option',
      sku: 'signet',
      swatch_url: signet,
      video_url: 'https://lashbrook-customizer-prod.s3.us-west-2.amazonaws.com/categories/Signet.mp4',
      selected: false
    });
  }
  if (show_collegiate_builder) {
    list.push({
      name: 'Collegiate',
      type: 'option',
      swatch_url: collegiateSignets,
      video_url: 'https://lashbrook-customizer-prod.s3.us-west-2.amazonaws.com/categories/Collegiate.mp4',
      selected: false,
      sku: 'collegiate_signet'
    });
  }
  return list.map((item) => {
    if (item.sku === sku || (item.sku === 'collegiate_signet' && sku === 'collegiate_bands')) {
      item.selected = true;
    }
    return item;
  });
};

export const getTypeOptions = (sku: string, showCollegiateBands?: boolean) => {
  if (sku === 'signet') {
    return [];
  } else {
    const list: IOption[] = [
      {
        name: 'Collegiate Signet',
        type: 'option',
        swatch_url: collegiateSignets,
        selected: false,
        sku: 'collegiate_signet'
      },
      ...(showCollegiateBands ? [{
        name: 'Collegiate Bands',
        type: 'option' as const,
        swatch_url: collegiateBand,
        selected: sku === 'collegiate_bands',
        sku: 'collegiate_bands',
      }] : []),
      // {
      //   name: 'Collegiate Sleeve',
      //   type: 'option',
      //   swatch_url: collegiateSleeve,
      //   selected: false,
      //   sku: 'collegiate_sleeve',
      // },
    ];
    return list.map((item) => {
      if (item.sku === sku) {
        item.selected = true;
      }
      return item;
    });
  }
};

export const DEFAULT_STYLES = {
  brandlogo: `url(${LashbrookLogo})`,
  brandlogoSecondary: `url(${LashbrookLogo})`,
  brandColor: '#2a77a7',
  // Accent rule under the active sidebar nav item. Defaults to the brand colour
  // so the Lashbrook fallback still looks deliberate.
  accentColor: '#2a77a7',
  // Hover state for the brand-coloured CTA buttons (Done, Search Reference Code,
  // Print, the popup actions). Defaults deliberately match the resting colours, so
  // the Lashbrook fallback keeps the no-op hover it has today.
  ctaHoverBgColor: '#2a77a7',
  ctaHoverTextColor: '#ffffff',
  primaryBgColor: '#f2f5f7',
  secondaryBgColor: '#ffffff',
  // Surface behind the desktop side nav. Split out from primaryBgColor because that
  // token also paints the rc-tabs nav bar below 1024px (see rc-tab.override.css), so
  // the two need to move independently. Defaults to primaryBgColor's value to keep
  // the Lashbrook fallback looking exactly as it did.
  sidebarBgColor: '#f2f5f7',
  // Fill behind the active section tab (1. PROFILE / 2. WIDTH and friends — the
  // .rc-box-active chip). White here keeps the Lashbrook fallback's flush-with-the-
  // panel look, where the tab reads as an extension of the content below it.
  tabActiveBgColor: '#ffffff',
  // Outline around the section tabs. Kept separate from primaryBorderColor because
  // that token also fills the active tab on some themes, and a border can't be seen
  // against its own colour. Defaults to primaryBorderColor's value so the Lashbrook
  // fallback keeps the gold outline it has today.
  tabBorderColor: '#DDCC7C',
  // The finger-size chips (SizeFilter). Themed as a set because the two themes disagree
  // on the whole treatment, not just a colour: these defaults are Lashbrook's outlined
  // white circles with grey numerals, where the fill is incidental and the ring is the
  // affordance.
  sizeOptionBgColor: '#ffffff',
  sizeOptionTextColor: '#c4c4c4',
  sizeOptionBorderColor: 'rgba(196, 196, 196, 0.5)',
  sizeOptionRadius: '50%',
  // Selected chip. The bg matches sizeOptionBgColor here, so on Lashbrook the selection
  // reads purely as the gold ring and the scale-up, exactly as it did before these
  // tokens existed.
  sizeOptionActiveBgColor: '#ffffff',
  sizeOptionActiveBorderColor: '#DDCC7C',
  // Scrollbar thumb, across the builder's scroll containers and Swiper's drag handle.
  // Defaults to what the category scroller already drew, so the Lashbrook fallback's
  // styled scrollbars are unchanged.
  scrollbarThumbColor: 'rgba(0, 0, 0, 0.5)',
  // Thumb corner rounding, applied to the native thumb, the category scroller's
  // thumb and Swiper's drag handle. Defaults to the 4px those rules already
  // hardcoded, so the Lashbrook fallback's scrollbars are unchanged.
  scrollbarRadius: '4px',
  primaryColor: '#003053',
  secondaryColor: '#323232',
  tertiaryColor: '#ffffff',
  primaryFont: 'Reckless',
  secondaryFont: 'Neue Haas Unica Pro',
  primaryBorderColor: '#DDCC7C',
  secondaryBorderColor: '#e9ecef',
  primaryBorderRadius: '8px',
  secondaryBorderRadius: '4px',
  tertiaryBorderRadius: '9999px',
  rcBoxTabCase: 'capitalize',
  sidebarOrder: '0',
  watermarkBrand: '',
  showMagicNumberAtDiscountPopup: false
};

// Keyed by the URL path prefix, i.e. the `:pathPrefix` segment of /:pathPrefix/rings/...
// A key that does not match the prefix the app is served under silently falls back
// to DEFAULT_STYLES (Lashbrook), so these keys must track the deployed URL exactly.
export const APP_STYLES: { [key: string]: typeof DEFAULT_STYLES } = {
  // Ben Bridge — the only tenant this fork serves. Keyed by PATH_PREFIX so the
  // theme can never drift from the route the app is served under.
  //
  // brandColor and the logo are confirmed Ben Bridge brand values. The tokens
  // marked TODO(benbridge-brand) below carried over from upstream's Helzberg
  // theme purely so the retail-partner layout (square corners, uppercase tabs,
  // right sidebar) renders instead of the Lashbrook default; they are NOT Ben
  // Bridge brand values and need replacing once brand guidelines land.
  [PATH_PREFIX]: {
    ...DEFAULT_STYLES,
    // Same asset for both slots: the wordmark only renders inside the dark promo
    // bar (see isLogoInBanner), where .banner-logo filters it to white, so there
    // is no separate light-background variant.
    brandlogo: `url(${BenBridgeLogo})`,
    brandlogoSecondary: `url(${BenBridgeLogo})`,
    brandColor: '#251024',
    // Rust accent rule under the active nav item, matching benbridge.com.
    accentColor: '#893A1C',
    // CTA hover. Note the text is pure #FFFFFF, deliberately not tertiaryColor's
    // #FDFDFD — these were specified separately.
    ctaHoverBgColor: '#72737B',
    ctaHoverTextColor: '#FFFFFF',
    primaryBgColor: '#FFFFFF',
    secondaryBgColor: '#FFFFFF',
    // Warm off-white behind the side nav, so it reads as a distinct panel against
    // the white page background rather than disappearing into it.
    sidebarBgColor: '#F8F7F3',
    // Same value as primaryBorderColor, which draws this chip's 2px border and the
    // rule it sits on — so the active tab reads as one solid warm-neutral tab rather
    // than an outlined white one. Written out rather than referencing the border
    // token: these are separate concerns that only happen to share a value today.
    tabActiveBgColor: '#EEECE9',
    // TODO(benbridge-brand): not a supplied brand value. A warm neutral one step down
    // from the #EEECE9 fill, chosen so the outline reads against both the white nav
    // bar (inactive tab) and the fill itself (active tab). Swap for the real value.
    tabBorderColor: '#D8D4CE',
    // Filled square chips, matching how the size options render on benbridge.com
    // product pages: the fill carries the chip, so there is no outline at rest and
    // every numeral is full-strength rather than greyed until selected.
    sizeOptionBgColor: '#EEECE9',
    sizeOptionTextColor: '#212121',
    sizeOptionBorderColor: 'transparent',
    sizeOptionRadius: '0',
    // Selection inverts the chip: the fill drops out to white and the outline arrives
    // in near-black, so the selected size reads against the row of warm-neutral chips.
    sizeOptionActiveBgColor: '#FFFFFF',
    sizeOptionActiveBorderColor: '#251024',
    // Brand colour, the same value as brandColor and the size chip's active
    // outline, rather than the lighter #532E63 carried over from upstream.
    scrollbarThumbColor: '#251024',
    // Square, matching the other radius tokens zeroed below.
    scrollbarRadius: '0',
    // Text drawn on top of brandColor: the Done buttons, the swatch "more info"
    // overlay, the promo bar message and the discount-popup buttons. This token is
    // never used as a background, so it is purely the on-brand-colour foreground.
    tertiaryColor: '#FDFDFD',
    // Confirmed Ben Bridge value. Drives body copy, swatch labels, the sidebar nav
    // rows, the section sub-headings and the rc-tab headings.
    primaryColor: '#251024',
    secondaryColor: '#909090', // TODO(benbridge-brand): upstream-inherited
    // Public Sans everywhere, loaded in public/index.html. Both slots point at it
    // rather than collapsing to one token, because DEFAULT_STYLES still uses a
    // display/body pair (Reckless / Neue Haas) and the two vars stay meaningful for
    // that fallback path.
    //
    // NB: this does NOT touch the engraving preview fonts (Lucida Calligraphy,
    // French Script, Old London, …). Those are customer-selected for the engraving
    // itself and are applied by the FONT_MAP classes in src/pages/index.scss.
    primaryFont: "'Public Sans', sans-serif",
    secondaryFont: "'Public Sans', sans-serif",
    // Warm neutral replacing the inherited blue-grey. Drives the rule between the
    // gallery and the options (.swatch-filter-border) and the vertical rule beside
    // the nav (#active-v-border), plus the rc-box tab rules.
    primaryBorderColor: '#EEECE9',
    // Outline on unselected option chips (SwatchOption's non-swatch path: engraving
    // fonts, widths, and the rest). Was #ABC2D6, an upstream blue-grey that read as the
    // one cool-toned line left in the builder. Now the same warm neutral as the section
    // tabs' outline. TODO(benbridge-brand): still not a supplied brand value.
    secondaryBorderColor: '#D8D4CE',
    primaryBorderRadius: '0',
    secondaryBorderRadius: '0',
    tertiaryBorderRadius: '0',
    rcBoxTabCase: 'uppercase',
    sidebarOrder: '1'
  }
};

// NOTE: each gate below was inherited switched-on from upstream's Helzberg
// config and is kept on for Ben Bridge, so the layout stays a recognisable
// retail-partner build. These gates are exactly what the two partners were
// expected to diverge on — revisit them per gate rather than assuming the
// inherited setting is right for Ben Bridge.
export const isRightSidebar = (pathPrefix: string) => {
  const rightSidebarClients = [PATH_PREFIX];

  return rightSidebarClients.includes(pathPrefix);
};

export const isDoneAtBottom = (pathPrefix: string) => {
  const clients = [PATH_PREFIX];

  return clients.includes(pathPrefix);
};

export const shouldHideSKU = (pathPrefix: string = '') => {
  const hiddenSKUclients = [PATH_PREFIX];

  return hiddenSKUclients.includes(pathPrefix);
};

// Ben Bridge: the wordmark is pinned to the left inside the promo bar, with the
// message centred on the bar independently of it. Distinct from the default
// banner layout, which centres the message relative to the logo and adds a
// second below-bar logo for phones.
export const isLogoInBanner = (pathPrefix: string = '') => {
  const clients = [PATH_PREFIX];

  return clients.includes(pathPrefix);
};

// The sidebar shows the running price on the summary row and on the Done button.
// Inherited switched-off from upstream's Helzberg config; kept as a named gate so
// it is reviewable rather than an inline pathPrefix comparison.
export const showPriceOnDone = (pathPrefix: string = '') => {
  const clients = [PATH_PREFIX];

  return clients.includes(pathPrefix);
};

const RING_PATH_PATTERNS = [
  '/rings/profiles/:profile/widths/:width/ring_materials/:ring_materials',
  '/:pathPrefix/rings/profiles/:profile/widths/:width/ring_materials/:ring_materials',
  '/women/rings/styles/:styles/ring_materials/:ring_materials/color_arrangements/:color_arrangements/stone_quantities/:stone_quantities/variants/:variants',
  '/:pathPrefix/women/rings/styles/:styles/ring_materials/:ring_materials/color_arrangements/:color_arrangements/stone_quantities/:stone_quantities/variants/:variants',
  '/rings/:ring_type/ring_materials/:ring_materials',
  '/:pathPrefix/rings/:ring_type/ring_materials/:ring_materials',
  '/rings/:ring_type/profiles/:profile/widths/:width/ring_materials/:ring_materials',
  '/:pathPrefix/rings/:ring_type/profiles/:profile/widths/:width/ring_materials/:ring_materials',
] as const;

export const urlToParams = (ring_url: string) => {
  const [pathString, queryString] = ring_url ? ring_url.split('?') : [];
  const pathname = pathString || '';
  let matchObj: ReturnType<typeof matchPath> | null = null;
  for (const pattern of RING_PATH_PATTERNS) {
    const m = matchPath({ path: pattern, end: true }, pathname);
    if (m) {
      matchObj = m;
      break;
    }
  }

  const searchParams = new URLSearchParams(queryString);
  const queryParams: IParams['queryParams'] = Object.fromEntries(searchParams.entries());
  const pathParams: IParams['pathParams'] = matchObj?.params || {};
  return { pathParams, queryParams };
};
